import { z } from 'zod';
const blockIdSchema = z.string().trim().min(1).max(100).regex(/^[A-Za-z0-9._:-]+$/);
const assetKeySchema = z.string().trim().min(1).max(1024).regex(/^tenants\/[^/]+\/message-assets\//);
export const messageTextBlockSchema = z.object({
    id: blockIdSchema,
    type: z.literal('text'),
    text: z.string().min(1).max(4096),
}).strict();
export const messageImageBlockSchema = z.object({
    id: blockIdSchema,
    type: z.literal('image'),
    assetKey: assetKeySchema,
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    fileName: z.string().trim().min(1).max(255),
    size: z.number().int().positive().max(10 * 1024 * 1024),
    caption: z.string().max(4096).optional(),
}).strict();
export const messageAudioBlockSchema = z.object({
    id: blockIdSchema,
    type: z.literal('audio'),
    assetKey: assetKeySchema,
    mimeType: z.enum(['audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/mp4', 'audio/webm']),
    fileName: z.string().trim().min(1).max(255),
    size: z.number().int().positive().max(16 * 1024 * 1024),
}).strict();
export const messageContentV1Schema = z.object({
    version: z.literal(1),
    blocks: z.array(z.discriminatedUnion('type', [
        messageTextBlockSchema,
        messageImageBlockSchema,
        messageAudioBlockSchema,
    ])).min(1).max(50),
}).strict().superRefine((document, ctx) => {
    const seen = new Set();
    document.blocks.forEach((block, index) => {
        if (seen.has(block.id)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['blocks', index, 'id'], message: 'block id must be unique' });
        }
        seen.add(block.id);
    });
});
function stableTextId(text) {
    let hash = 0x811c9dc5;
    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return `legacy-text-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}
export function messageContentFromLegacyText(text) {
    if (!text.trim())
        throw new Error('Legacy message text cannot be empty');
    return { version: 1, blocks: [{ id: stableTextId(text), type: 'text', text }] };
}
export function parseMessageContent(document) {
    return messageContentV1Schema.parse(document);
}
export function normalizeMessageContent(document, legacyText) {
    return document == null ? messageContentFromLegacyText(legacyText) : parseMessageContent(document);
}
/** Safe rollback fallback: text blocks and image captions only; never serialized JSON. */
export function messageContentFallbackText(document) {
    const parts = document.blocks.flatMap((block) => {
        if (block.type === 'text')
            return [block.text];
        if (block.type === 'image' && block.caption?.trim())
            return [block.caption];
        return [];
    });
    return parts.join('\n\n').trim() || '[Conteúdo de mídia]';
}
export function assertMessageAssetsBelongToTenant(document, tenantId) {
    const prefix = `tenants/${tenantId}/message-assets/`;
    for (const block of document.blocks) {
        if (block.type !== 'text' && !block.assetKey.startsWith(prefix)) {
            throw new Error('Message asset does not belong to the caller tenant');
        }
    }
}
//# sourceMappingURL=message-content.js.map